import React from 'react';
import { T } from './_theme';

// Build a `/?mode=reading-home` URL that preserves the currently
// selected examId. Without this, every Back button would dump the user
// back to a generic reading-home that re-resolves to whichever exam
// resolveReadingExam() finds first — meaning students who picked exam B
// then went Back ended up on exam A. Read examId from the live URL
// because part screens don't otherwise know it.
export function readingHomeHref() {
  if (typeof window === 'undefined') return '/?mode=reading-home';
  let examId = '';
  try {
    examId = new URLSearchParams(window.location.search).get('examId') || '';
  } catch {
    examId = '';
  }
  return examId
    ? `/?mode=reading-home&examId=${encodeURIComponent(examId)}`
    : '/?mode=reading-home';
}

// ─────────────────────────────────────────────────────────────────────
// Shared reading-screen primitives — direct ports of the gallery's
// Pill / OptionBtn / FeedbackCard / TopBar / LangToggle so every part
// screen renders consistently.
// ─────────────────────────────────────────────────────────────────────

export function Pill({ children, tone = 'neutral', size = 's' }) {
  const tones = {
    neutral: { bg: T.bgSunk, fg: T.ink2, bd: T.hairline },
    blue:    { bg: T.blueBg, fg: T.blueInk, bd: T.blueEdge },
    ok:      { bg: T.okBg, fg: T.ok, bd: T.okEdge },
    miss:    { bg: T.missBg, fg: T.miss, bd: T.missEdge },
    hint:    { bg: T.hintBg, fg: T.hint, bd: T.hintEdge },
  }[tone];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: tones.bg, color: tones.fg,
      border: `1px solid ${tones.bd}`,
      padding: size === 's' ? '3px 9px' : '5px 12px',
      borderRadius: 9999,
      fontSize: size === 's' ? 11 : 12,
      fontWeight: 600, fontFamily: T.sans, lineHeight: 1.2, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

export function PrimaryBtn({ children, onClick, disabled, type = 'button', full = true }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      width: full ? '100%' : 'auto',
      padding: '14px 20px',
      background: disabled ? T.bgSunk : T.blue,
      color: disabled ? T.ink4 : '#fff',
      fontSize: 15, fontWeight: 600,
      borderRadius: 14, border: 0,
      cursor: disabled ? 'not-allowed' : 'pointer',
      boxShadow: disabled ? 'none' : '0 1px 2px rgba(62,111,176,.2), 0 4px 12px rgba(62,111,176,.15)',
      fontFamily: T.sans,
    }}>{children}</button>
  );
}

export function GhostBtn({ children, onClick, tone = 'neutral', disabled, type = 'button' }) {
  const c = {
    neutral: { bg: T.surface, fg: T.ink, bd: T.hairlineInk },
    hint:    { bg: T.hintBg, fg: T.hint, bd: T.hintEdge },
  }[tone];
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{
      padding: '10px 14px',
      background: c.bg, color: c.fg,
      border: `1px solid ${c.bd}`,
      borderRadius: 14,
      fontSize: 14, fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily: T.sans,
      opacity: disabled ? 0.55 : 1,
    }}>{children}</button>
  );
}

// MCQ option row — letter chip + text. State drives styling:
//   idle | selected | correct | incorrect | dim
export function OptionBtn({ letter, state = 'idle', onClick, disabled, children }) {
  const s = {
    idle:      { bg: T.surface, bd: T.hairlineInk, fg: T.ink, bBg: T.bgSunk, bFg: T.ink2 },
    selected:  { bg: T.blueBg, bd: T.blue, fg: T.ink, bBg: T.blue, bFg: '#fff' },
    correct:   { bg: T.okBg, bd: T.ok, fg: T.ink, bBg: T.ok, bFg: '#fff' },
    incorrect: { bg: T.missBg, bd: T.miss, fg: T.ink, bBg: T.miss, bFg: '#fff' },
    dim:       { bg: T.surface, bd: T.hairline, fg: T.ink3, bBg: T.bgSunk, bFg: T.ink3 },
  }[state];
  const glyph = state === 'correct' ? '✓' : state === 'incorrect' ? '✕' : letter;
  return (
    <button type="button" onClick={onClick} disabled={disabled || state !== 'idle' && state !== 'selected'} style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: 14,
      background: s.bg,
      border: `1.5px solid ${s.bd}`,
      borderRadius: 14,
      color: s.fg,
      fontSize: 15, lineHeight: 1.45,
      opacity: state === 'dim' ? 0.75 : 1,
      cursor: disabled ? 'not-allowed' : (state === 'idle' || state === 'selected') ? 'pointer' : 'default',
      width: '100%', textAlign: 'left',
      fontFamily: T.sans,
    }}>
      <span style={{
        flexShrink: 0, width: 28, height: 28, borderRadius: 14,
        background: s.bBg, color: s.bFg,
        display: 'grid', placeItems: 'center',
        fontSize: 13, fontWeight: 700,
      }}>{glyph}</span>
      <span style={{ flex: 1, paddingTop: 4 }}>{children}</span>
    </button>
  );
}

export function LangToggle({ lang = 'en', onChange }) {
  return (
    <div style={{
      display: 'inline-flex', background: T.bgSunk, borderRadius: 9999,
      padding: 3, border: `1px solid ${T.hairline}`,
    }}>
      {['en', 'ar'].map((v) => (
        <button
          key={v}
          type="button"
          onClick={onChange ? () => onChange(v) : undefined}
          style={{
            padding: '4px 12px', borderRadius: 9999,
            fontSize: 12, fontWeight: 600,
            background: lang === v ? T.surface : 'transparent',
            color: lang === v ? T.ink : T.ink3,
            boxShadow: lang === v ? '0 1px 2px rgba(70,55,30,.04)' : 'none',
            border: 'none', cursor: 'pointer', fontFamily: T.sans,
          }}
        >{v.toUpperCase()}</button>
      ))}
    </div>
  );
}

// Per-part top bar with progress strip + optional right slot.
export function PartTopBar({ partNumber, partTitle, currentIdx, total, onBack }) {
  const pct = total > 0 ? ((currentIdx + 1) / total) * 100 : 0;
  return (
    <div style={{ background: T.bg, borderBottom: `1px solid ${T.hairline}`, padding: '14px 16px 12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          type="button"
          onClick={onBack}
          aria-label="Back"
          style={{
            width: 36, height: 36, borderRadius: 18,
            background: T.surface, border: `1px solid ${T.hairline}`,
            display: 'grid', placeItems: 'center',
            color: T.ink2, fontSize: 14, cursor: 'pointer', fontFamily: T.sans,
          }}
        >‹</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, letterSpacing: -0.2, fontFamily: T.sans }}>
            Part {partNumber} · {partTitle}
          </div>
          <div style={{ fontSize: 12, color: T.ink3, marginTop: 1, fontFamily: T.sans }}>
            Question {currentIdx + 1} of {total}
          </div>
        </div>
      </div>
      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, height: 4, background: T.bgSunk, borderRadius: 2, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: T.blue, transition: 'width .25s ease' }} />
        </div>
        <div style={{ fontSize: 11, color: T.ink3, fontFamily: T.mono }}>
          {currentIdx + 1}/{total}
        </div>
      </div>
    </div>
  );
}

// Reading-section frame — warm beige page, max-width responsive container.
export function ReadingFrame({ children, isMobile }) {
  return (
    <div style={{
      minHeight: '100vh',
      background: T.bg,
      fontFamily: T.sans,
      color: T.ink,
    }}>
      <div style={{
        maxWidth: isMobile ? '100%' : 720,
        margin: '0 auto',
        minHeight: '100vh',
        background: T.bg,
        display: 'flex',
        flexDirection: 'column',
      }}>{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// FeedbackCard — bilingual learning moment after a wrong/right answer.
// Mirrors the gallery's structured card: header, "why it works", "you
// picked X" (only when wrong), "try these", "from the text".
// ─────────────────────────────────────────────────────────────────────
export function FeedbackCard({ correct, header, lang = 'en', onLangChange, simple, mistake, picked, examples, evidence }) {
  const isRtl = lang === 'ar';
  return (
    <div style={{
      background: T.surface,
      borderRadius: 20,
      border: `1px solid ${correct ? T.okEdge : T.missEdge}`,
      boxShadow: '0 1px 3px rgba(70,55,30,.06), 0 4px 14px rgba(70,55,30,.05)',
      overflow: 'hidden',
      marginTop: 20,
    }}>
      <div dir={isRtl ? 'rtl' : 'ltr'} style={{
        background: correct ? T.okBg : T.missBg,
        padding: '14px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${correct ? T.okEdge : T.missEdge}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            width: 26, height: 26, borderRadius: 13,
            display: 'grid', placeItems: 'center',
            background: correct ? T.ok : T.miss,
            color: '#fff', fontSize: 14, fontWeight: 700,
          }}>{correct ? '✓' : '!'}</div>
          <span style={{
            fontSize: 15, fontWeight: 600,
            color: correct ? T.ok : T.miss,
            fontFamily: isRtl ? T.ar : T.sans,
          }}>{header}</span>
        </div>
        <LangToggle lang={lang} onChange={onLangChange} />
      </div>
      {simple && (
        <FBS label={lang === 'en' ? 'Why it works' : 'السبب'} dir={isRtl ? 'rtl' : 'ltr'}>
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, fontFamily: isRtl ? T.ar : T.sans }}>{simple}</p>
        </FBS>
      )}
      {!correct && mistake && (
        <FBS label={lang === 'en' ? `You picked ${picked}` : `اخترت ${picked}`} dir={isRtl ? 'rtl' : 'ltr'} tone="miss">
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.7, color: T.ink2, fontFamily: isRtl ? T.ar : T.sans }}>{mistake}</p>
        </FBS>
      )}
      {Array.isArray(examples) && examples.length > 0 && (
        <FBS label={lang === 'en' ? 'Try these' : 'أمثلة'} dir={isRtl ? 'rtl' : 'ltr'}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {examples.map((ex, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                background: ex.bad ? T.missBg : T.okBg,
                border: `1px solid ${ex.bad ? T.missEdge : T.okEdge}`,
                borderRadius: 10, padding: '10px 12px',
              }}>
                <span style={{
                  flexShrink: 0, width: 18, height: 18, borderRadius: 9,
                  display: 'grid', placeItems: 'center',
                  background: ex.bad ? T.miss : T.ok, color: '#fff',
                  fontSize: 11, fontWeight: 700, marginTop: 2,
                }}>{ex.bad ? '✕' : '✓'}</span>
                <span style={{ fontSize: 14, lineHeight: 1.5, fontFamily: isRtl ? T.ar : T.sans }}>{ex.text || ex.t}</span>
              </div>
            ))}
          </div>
        </FBS>
      )}
      {evidence && (
        <FBS label={lang === 'en' ? 'From the text' : 'من النص'} dir={isRtl ? 'rtl' : 'ltr'} tone="hint" last>
          <div style={{
            borderInlineStart: `3px solid ${T.hint}`,
            paddingInlineStart: 10,
            fontSize: 14, lineHeight: 1.55,
            color: T.ink2, fontStyle: 'italic',
            fontFamily: isRtl ? T.ar : T.sans,
          }}>“{evidence}”</div>
        </FBS>
      )}
    </div>
  );
}

function FBS({ label, children, tone = 'neutral', last, dir }) {
  const bg = { neutral: 'transparent', miss: T.missBg, hint: T.hintBg }[tone];
  return (
    <div dir={dir} style={{
      padding: '14px 16px',
      borderBottom: last ? 'none' : `1px solid ${T.hairline}`,
      background: bg,
    }}>
      <div style={{
        fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8,
        fontWeight: 700, color: T.ink3, marginBottom: 8,
        fontFamily: dir === 'rtl' ? T.ar : T.sans,
      }}>{label}</div>
      {children}
    </div>
  );
}

// Hint banner — used to nudge the student to answer before going next.
export function NudgeBanner({ children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      background: T.hintBg, border: `1px solid ${T.hintEdge}`,
      color: T.hint, borderRadius: 12,
      padding: '10px 14px', marginBottom: 12,
      fontSize: 13, fontWeight: 600, fontFamily: T.sans,
    }}>
      <span aria-hidden="true">👉</span>
      <span>{children}</span>
    </div>
  );
}

export function readArabicPref() {
  try {
    const v = window.localStorage.getItem('fet_reading_arabic');
    return v === null ? true : v === '1';
  } catch { return true; }
}
